// The structured dream payload, sealed into an entry's oblivion_cipher.
//
// iOS owns this format. It is `OblivionPayload` in ios-app/.../Core/Storage/OreiaEntry.swift, encoded
// with a plain `JSONEncoder()` — no key strategy — so the wire keys are the Swift property names
// verbatim, in camelCase, and the enums travel as their lowercase raw values.
//
// Matching it exactly is not tidiness. iOS decodes the whole blob in one `JSONDecoder().decode` call,
// and Swift's synthesised decoder reads a defaulted property with `decodeIfPresent`: a key that is
// absent falls back to the default, but a key that is present with the wrong JSON type throws. One
// throw fails the entire decode, `EntryCrypto.decryptJSON` returns nil, and `entry.oblivion` is nil —
// so a single mistyped field does not degrade one detail, it silently erases every structured detail
// on that dream as far as the phone is concerned. The previous version of this type declared `setting`
// and `atmosphere` as strings where iOS has `[String]`, which is precisely that bug, and it was only
// harmless because nothing in the browser wrote a dream payload yet.

/** iOS `DreamTone`. */
export type DreamTone = 'bad' | 'neutral' | 'good';

/** iOS `DreamPerspective`. */
export type DreamPerspective = 'first' | 'third' | 'switching';

/**
 * Every field of iOS's `OblivionPayload`, in the same shapes.
 *
 * Nothing is optional. iOS gives each property a default and always writes all of them, so a payload
 * is either absent entirely or complete — and modelling that as "always present" keeps the composer
 * from threading `undefined` through every binding.
 */
export interface DreamDetails {
  setting: string[];
  atmosphere: string[];
  emotions: string[];
  elements: string[];
  characters: string[];
  /** Free text: what actually happened. */
  happened: string;
  /** 1–5. */
  lucidity: number;
  /** 1–5. */
  vividness: number;
  tone: DreamTone;
  perspective: DreamPerspective;
  sensoryAnchors: string[];
  realityCheck: string;
  themes: string[];
  actions: string[];
  /** Precognitive, Message, Symbolic… plus anything the user typed. */
  dreamNature: string[];
  /** 0 means unset, otherwise 1–5. */
  sleepQuality: number;
  recurring: boolean;
  /** Myself, Someone else, An animal… plus anything the user typed. */
  dreamedAs: string[];
  /** Oreia's one-line reasoning from "read the tone". */
  toneNote: string;
  /**
   * Web-only: Oreia has already filled these pages, or the dreamer asked it to.
   *
   * Absent on anything iOS wrote, and iOS will ignore the key if it meets one. It is optional on
   * purpose — adding it as a required field would put `"oreiaFilled": false` on every payload, and
   * a round-trip of an iOS entry would no longer be bit-identical. Stored so a later save cannot
   * silently re-extract: appending is how a tag the dreamer deleted used to come back.
   */
  oreiaFilled?: boolean;
  /**
   * Web-only: the catalogue stem of the picture the dreamer chose as this entry's cover
   * ("phone", "forest"). Absent means Oreia's automatic pick from the symbols. A photo they
   * uploaded or imagined still wins — this is only the stand-in.
   */
  coverArt?: string;
}

/**
 * The defaults iOS declares. `lucidity: 2`, `vividness: 3`, `tone: .neutral` and `perspective: .first`
 * are not neutral-looking placeholders — `hasStructuredDreamData` treats a departure from any of them
 * as evidence the user touched the form, so they have to agree with Swift exactly.
 */
export function emptyDreamDetails(): DreamDetails {
  return {
    setting: [],
    atmosphere: [],
    emotions: [],
    elements: [],
    characters: [],
    happened: '',
    lucidity: 2,
    vividness: 3,
    tone: 'neutral',
    perspective: 'first',
    sensoryAnchors: [],
    realityCheck: '',
    themes: [],
    actions: [],
    dreamNature: [],
    sleepQuality: 0,
    recurring: false,
    dreamedAs: [],
    toneNote: '',
  };
}

const KNOWN_KEYS = new Set<string>([...Object.keys(emptyDreamDetails()), 'oreiaFilled', 'coverArt']);

const TONES: readonly DreamTone[] = ['bad', 'neutral', 'good'];
const PERSPECTIVES: readonly DreamPerspective[] = ['first', 'third', 'switching'];

/**
 * A list of non-empty strings.
 *
 * A bare string is accepted and wrapped, because an early version of this client modelled `setting`
 * and `atmosphere` as single strings and may have written entries in that shape.
 */
function strings(value: unknown): string[] {
  if (typeof value === 'string') {
    const one = value.trim();
    return one ? [one] : [];
  }
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.trim() !== '').map((v) => v.trim());
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function int(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback;
}

function bool(value: unknown): boolean {
  // Swift encodes a Bool as true/false, but a row that has been through a numeric column at some
  // point can arrive as 1/0.
  return value === true || value === 1;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * Normalise a decrypted blob into a complete `DreamDetails`.
 *
 * Unrecognised keys are carried through untouched. When a later version of iOS adds a field, an edit
 * made in the browser must not be the thing that deletes it: the payload is re-sealed wholesale on
 * save, so anything dropped here is dropped from the phone too.
 */
export function parseDreamDetails(raw: unknown): DreamDetails {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptyDreamDetails();
  const o = raw as Record<string, unknown>;

  const carried: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(o)) {
    if (!KNOWN_KEYS.has(key)) carried[key] = value;
  }

  return {
    ...carried,
    setting: strings(o.setting),
    atmosphere: strings(o.atmosphere),
    emotions: strings(o.emotions),
    elements: strings(o.elements),
    characters: strings(o.characters),
    happened: text(o.happened),
    lucidity: int(o.lucidity, 2),
    vividness: int(o.vividness, 3),
    tone: oneOf(o.tone, TONES, 'neutral'),
    perspective: oneOf(o.perspective, PERSPECTIVES, 'first'),
    sensoryAnchors: strings(o.sensoryAnchors),
    realityCheck: text(o.realityCheck),
    themes: strings(o.themes),
    actions: strings(o.actions),
    dreamNature: strings(o.dreamNature),
    sleepQuality: int(o.sleepQuality, 0),
    recurring: bool(o.recurring),
    dreamedAs: strings(o.dreamedAs),
    toneNote: text(o.toneNote),
    // Only the true is kept. Writing `false` would change every iOS payload we re-seal, and a key
    // that means "not yet" is the same as the key being absent.
    ...(o.oreiaFilled === true || o.oreiaFilled === 1 ? { oreiaFilled: true } : {}),
    ...(typeof o.coverArt === 'string' && o.coverArt.trim() ? { coverArt: o.coverArt.trim() } : {}),
  };
}

/**
 * Whether the dreamer (or an extraction) actually logged something on the pages.
 *
 * Matches iOS's `OblivionPayload.hasStructuredData`, including its omission of `toneNote`.
 * `oreiaFilled` is not counted here: a flag that Oreia has already had its pass is not a setting,
 * and sending "Tone: neutral; lucidity 2" to a reading would have the model treat composer defaults
 * as things the dreamer reported.
 */
export function hasLoggedDreamDetails(d: DreamDetails): boolean {
  return (
    d.setting.length > 0 ||
    d.atmosphere.length > 0 ||
    d.emotions.length > 0 ||
    d.elements.length > 0 ||
    d.characters.length > 0 ||
    d.happened !== '' ||
    d.themes.length > 0 ||
    d.actions.length > 0 ||
    d.dreamNature.length > 0 ||
    d.sensoryAnchors.length > 0 ||
    d.realityCheck !== '' ||
    d.dreamedAs.length > 0 ||
    d.recurring ||
    d.sleepQuality > 0 ||
    d.lucidity !== 2 ||
    d.vividness !== 3 ||
    d.tone !== 'neutral' ||
    d.perspective !== 'first'
  );
}

/**
 * Whether there is anything worth sealing alongside the story.
 *
 * The logged pages, the web-only `oreiaFilled` flag, or a chosen cover. `oreiaFilled` has to
 * survive a save even when the form is empty, or clearing the tags would look like an untouched
 * dream and get silently re-filled. `coverArt` is the same kind of web-only wish: without it a
 * cover-only payload would be dropped on save. When this is false the entry is saved with no
 * oblivion_cipher at all, which is how clearing the form removes the details on both clients —
 * unless Oreia has already had its pass, or a cover was chosen.
 */
export function hasStructuredDreamData(d: DreamDetails): boolean {
  return d.oreiaFilled === true || Boolean(d.coverArt) || hasLoggedDreamDetails(d);
}

/**
 * Words that carry no image on their own. A phrase made only of these is a fragment the extraction
 * should not have handed us — "it", "the one" — and a chip reading "It" tells the dreamer nothing.
 */
const EMPTY_PHRASES = new Set([
  'it', 'this', 'that', 'these', 'those', 'they', 'them', 'he', 'she', 'him', 'her', 'we', 'us',
  'i', 'me', 'my', 'you', 'your', 'one', 'thing', 'things', 'something', 'someone', 'somewhere',
  'a', 'an', 'the', 'there', 'here', 'some', 'any', 'other', 'others',
]);

/** Longer than this and it is a sentence, not a symbol — it would blow out the chip row. */
const MAX_CHIP_LENGTH = 28;

/** As many chips as fit two rows. Past that the row stops being scannable. */
const MAX_CHIPS = 6;

/**
 * Symbol chips for a dream, drawn from the structured pages: key elements first, then settings.
 *
 * The phrase is kept whole. An earlier version reduced each one to its last word, which read fine
 * for "a folding staircase" but quietly destroyed the meaning everywhere else — "a black bag" became
 * "Bag", "the cleaning lady" became "Lady", and anything ending in a pronoun became "It". The
 * adjective is usually the part the dream was about, so only the leading article comes off.
 *
 * Mirrored in iOS's `ComposerView.deriveSymbols`, so a dream chipped on one client reads the same on
 * the other.
 */
export function deriveSymbols(details: Pick<DreamDetails, 'elements' | 'setting'>): string[] {
  const seen = new Set<string>();
  const chips: string[] = [];

  for (const fragment of [...details.elements, ...details.setting]) {
    const phrase = fragment.trim().replace(/\s+/g, ' ').replace(/^(?:a|an|the)\s+/i, '');
    if (phrase.length < 2 || phrase.length > MAX_CHIP_LENGTH) continue;

    const words = phrase.toLowerCase().split(' ');
    if (words.every((word) => EMPTY_PHRASES.has(word.replace(/[^a-z]/g, '')))) continue;

    const key = phrase.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    chips.push(phrase.charAt(0).toUpperCase() + phrase.slice(1));
    if (chips.length === MAX_CHIPS) break;
  }

  return chips;
}

/**
 * How Oreia classifies a dream from its characteristics — iOS's `derivedDreamType`.
 *
 * Kept identical so the cleartext `dream_type` column, which the server groups and filters on, does not
 * disagree with itself depending on which device saved the entry last.
 */
export function derivedDreamType(d: DreamDetails): 'normal' | 'lucid' | 'nightmare' {
  if (d.lucidity >= 4) return 'lucid';
  if (d.tone === 'bad') return 'nightmare';
  return 'normal';
}
